import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { getCourseById, subscribeToCourseSections, createSection, updateSection, deleteSection, subscribeToCourseMaterials, createMaterial, deleteMaterial, subscribeToCourseNotes, createCourseNote, deleteCourseNote, updateCourseNote } from '../services/firestore'
import { ArrowLeft, Plus, Clock, MapPin, Edit, Trash2, Calendar, X, Save, Video, FileText, Book, Microscope, StickyNote as StickyNoteIcon } from 'lucide-react'
import MaterialCard from '../components/MaterialCard'
import StickyNote from '../components/StickyNote'
import './CourseDetail.css'

const initialFormState = {
  day: '',
  startTime: '',
  endTime: '',
  building: '',
  classroom: '',
  meetingLink: '',
  topic: '',
  date: ''
}

const CourseDetail = () => {
  const { courseId } = useParams()
  const { user, tutorProfile } = useAuth()
  const navigate = useNavigate()
  const [course, setCourse] = useState(null)
  const [sections, setSections] = useState([])
  const [loading, setLoading] = useState(true)
  const [showSectionForm, setShowSectionForm] = useState(false)
  const [editingSection, setEditingSection] = useState(null)
  const [formData, setFormData] = useState(initialFormState)
  const [error, setError] = useState('')
  const isEUCS = tutorProfile?.tutorType === 'eucs'

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    try {
      const parts = dateStr.split('-')
      if (parts.length !== 3) return dateStr
      const [, month, day] = parts
      const dayInt = parseInt(day)
      const monthInt = parseInt(month)
      if (isNaN(dayInt) || isNaN(monthInt) || monthInt < 1 || monthInt > 12) {
        return dateStr
      }
      const months = ['de Enero', 'de Febrero', 'de Marzo', 'de Abril', 'de Mayo', 'de Junio', 'de Julio', 'de Agosto', 'de Septiembre', 'de Octubre', 'de Noviembre', 'de Diciembre']
      return `${dayInt} ${months[monthInt - 1]}`
    } catch {
      return dateStr
    }
  }

  // Materials & Notes state
  const [activeTab, setActiveTab] = useState('secciones') // 'secciones', 'materiales'
  const [materials, setMaterials] = useState([])
  const [notes, setNotes] = useState([])
  const [showMaterialForm, setShowMaterialForm] = useState(false)
  const [showNoteForm, setShowNoteForm] = useState(false)
  const [materialForm, setMaterialForm] = useState({ type: 'video', title: '', url: '', description: '' })
  const [noteForm, setNoteForm] = useState({ content: '', color: '#FFD700', pinned: false })

  useEffect(() => {
    loadCourse()
    const unsubscribe = subscribeToCourseSections(courseId, (result) => {
      if (result.success) {
        setSections(result.data)
      }
    })

    return () => unsubscribe()
  }, [courseId])

  // Load materials and notes
  useEffect(() => {
    const unsubMaterials = subscribeToCourseMaterials(courseId, (result) => {
      if (result.success) {
        setMaterials(result.data)
      }
    })

    const unsubNotes = subscribeToCourseNotes(courseId, (result) => {
      if (result.success) {
        setNotes(result.data)
      }
    })

    return () => {
      unsubMaterials()
      unsubNotes()
    }
  }, [courseId])

  const loadCourse = async () => {
    try {
      const result = await getCourseById(courseId)
      if (result.success) {
        setCourse(result.data)
      }
    } catch (err) {
      console.error('Error loading course:', err)
    }
    setLoading(false)
  }

  const isTutorOfCourse = course && course.tutors?.includes(user?.uid)

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (isEUCS) {
      if (!formData.date || !formData.day || !formData.startTime || !formData.endTime || !formData.meetingLink || !formData.topic) {
        setError('Todos los campos son requeridos para clases EUCS (incluyendo Fecha y Sílabo)')
        return
      }
    } else {
      if (!formData.day || !formData.startTime || !formData.endTime || !formData.building || !formData.classroom) {
        setError('Todos los campos son requeridos')
        return
      }
    }

    // Validar que la hora de fin sea posterior a la hora de inicio
    const startHour = parseInt(formData.startTime.split(':')[0])
    const startMinute = parseInt(formData.startTime.split(':')[1].split(' ')[0])
    const startPeriod = formData.startTime.split(' ')[1]
    const endHour = parseInt(formData.endTime.split(':')[0])
    const endMinute = parseInt(formData.endTime.split(':')[1].split(' ')[0])
    const endPeriod = formData.endTime.split(' ')[1]

    let startTime24 = startHour === 12 ? 0 : startHour
    if (startPeriod === 'PM') startTime24 += 12
    let endTime24 = endHour === 12 ? 0 : endHour
    if (endPeriod === 'PM') endTime24 += 12

    const startMinutes = startTime24 * 60 + startMinute
    const endMinutes = endTime24 * 60 + endMinute

    if (endMinutes <= startMinutes) {
      setError('La hora de fin debe ser posterior a la hora de inicio')
      return
    }

    // Combinar campos para guardar en la base de datos
    const sectionData = {
      day: formData.day,
      date: formData.date,
      time: `${formData.startTime} - ${formData.endTime}`
    }

    if (isEUCS) {
      sectionData.location = 'Virtual'
      sectionData.meetingLink = formData.meetingLink
      sectionData.topic = formData.topic
    } else {
      sectionData.location = `Edificio ${formData.building} Aula ${formData.classroom}`
      // Limpiar campos virtuales para evitar remanentes en Firestore
      sectionData.meetingLink = null
      sectionData.topic = null
    }

    try {
      if (editingSection) {
        // Actualizar sección
        const result = await updateSection(editingSection.id, sectionData)
        if (result.success) {
          setEditingSection(null)
          setShowSectionForm(false)
          setFormData(initialFormState)
        } else {
          setError(result.error || 'Error al actualizar la sección')
        }
      } else {
        // Crear nueva sección
        const result = await createSection(sectionData, courseId, user.uid)
        if (result.success) {
          setShowSectionForm(false)
          setFormData(initialFormState)
        } else {
          setError(result.error || 'Error al crear la sección')
        }
      }
    } catch (err) {
      setError(err.message || 'Error al guardar la sección')
    }
  }

  const handleEdit = (section) => {
    setEditingSection(section)

    // Parsear el tiempo y ubicación existentes
    const timeParts = section.time?.split(' - ') || ['', '']
    const locationParts = section.location?.match(/Edificio (.+?) Aula (.+)/) || []

    setFormData({
      day: section.day || '',
      startTime: timeParts[0] || '',
      endTime: timeParts[1] || '',
      building: locationParts[1] || '',
      classroom: locationParts[2] || '',
      meetingLink: section.meetingLink || '',
      topic: section.topic || '',
      date: section.date || ''
    })
    setShowSectionForm(true)
  }

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta sección?')) {
      return
    }

    try {
      const result = await deleteSection(sectionId)
      if (!result.success) {
        setError(result.error || 'Error al eliminar la sección')
      }
    } catch (err) {
      setError(err.message || 'Error al eliminar la sección')
    }
  }

  // Material handlers
  const handleAddMaterial = async (e) => {
    e.preventDefault()
    if (!materialForm.title || !materialForm.url) {
      setError('Título y URL son requeridos')
      return
    }

    try {
      const result = await createMaterial(materialForm, courseId, user.uid)
      if (result.success) {
        setShowMaterialForm(false)
        setMaterialForm({ type: 'video', title: '', url: '', description: '' })
        setError('')
      } else {
        setError(result.error)
      }
    } catch (err) {
      setError(err.message)
    }
  }

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('¿Eliminar este material?')) return

    try {
      await deleteMaterial(materialId)
    } catch (err) {
      setError(err.message)
    }
  }

  // Note handlers
  const handleAddNote = async (e) => {
    e.preventDefault()
    if (!noteForm.content) {
      setError('El contenido de la nota es requerido')
      return
    }

    try {
      const result = await createCourseNote(noteForm, courseId, user.uid)
      if (result.success) {
        setShowNoteForm(false)
        setNoteForm({ content: '', color: '#FFD700', pinned: false })
        setError('')
      } else {
        setError(result.error)
      }
    } catch (err) {
      setError(err.message)
    }
  }

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('¿Eliminar esta nota?')) return

    try {
      await deleteCourseNote(noteId)
    } catch (err) {
      setError(err.message)
    }
  }

  const handleTogglePin = async (noteId, pinned) => {
    try {
      await updateCourseNote(noteId, { pinned })
    } catch (err) {
      setError(err.message)
    }
  }

  // Group materials by type
  const materialsByType = {
    video: materials.filter(m => m.type === 'video'),
    ejercicio: materials.filter(m => m.type === 'ejercicio'),
    libro: materials.filter(m => m.type === 'libro'),
    simulador: materials.filter(m => m.type === 'simulador')
  }

  const handleCancel = () => {
    setShowSectionForm(false)
    setEditingSection(null)
    setFormData(initialFormState)
    setError('')
  }

  if (loading) {
    return (
      <div className="course-detail-page">
        <div className="loading">Cargando...</div>
      </div>
    )
  }

  if (!course) {
    return (
      <div className="course-detail-page">
        <div className="error">Curso no encontrado</div>
      </div>
    )
  }

  return (
    <div className="course-detail-page">
      <div className="course-detail-container">
        <button
          className="back-button"
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft size={20} />
          Volver
        </button>

        <div className="course-header">
          <h1>{course.name}</h1>
          <p className="description">{course.description || 'Sin descripción'}</p>
        </div>

        {/* Tabs */}
        <div className="tabs-container">
          <button
            className={`tab ${activeTab === 'secciones' ? 'active' : ''}`}
            onClick={() => setActiveTab('secciones')}
          >
            <Calendar size={18} />
            Secciones
          </button>
          <button
            className={`tab ${activeTab === 'materiales' ? 'active' : ''}`}
            onClick={() => setActiveTab('materiales')}
          >
            <Book size={18} />
            Materiales
          </button>
        </div>

        {/* Secciones Tab */}
        {activeTab === 'secciones' && (
          <>
            {isTutorOfCourse && (
              <div className="actions-bar">
                <button
                  className="add-section-button"
                  onClick={() => {
                    setShowSectionForm(true)
                    setEditingSection(null)
                    setFormData(initialFormState)
                  }}
                >
                  <Plus size={20} />
                  Agregar Sección
                </button>
              </div>
            )}
          </>
        )}

        {/* Materiales Tab */}
        {activeTab === 'materiales' && (
          <div className="materials-container">
            {/* Sticky Notes */}
            {notes.length > 0 && (
              <div className="sticky-notes-section">
                <h3>📌 Notas Importantes</h3>
                <div className="sticky-notes-grid">
                  {notes.map(note => (
                    <StickyNote
                      key={note.id}
                      note={note}
                      onEdit={() => { }}
                      onDelete={handleDeleteNote}
                      onTogglePin={handleTogglePin}
                      isOwner={user?.uid === note.createdBy}
                    />
                  ))}
                </div>
              </div>
            )}

            {isTutorOfCourse && (
              <div className="materials-actions">
                <button
                  className="add-material-button"
                  onClick={() => setShowMaterialForm(!showMaterialForm)}
                >
                  <Plus size={18} />
                  Agregar Material
                </button>
                <button
                  className="add-note-button"
                  onClick={() => setShowNoteForm(!showNoteForm)}
                >
                  <StickyNoteIcon size={18} />
                  Agregar Nota
                </button>
              </div>
            )}

            {/* Material Form */}
            {showMaterialForm && (
              <form className="material-form" onSubmit={handleAddMaterial}>
                <h3>Nuevo Material</h3>
                <select
                  value={materialForm.type}
                  onChange={(e) => setMaterialForm({ ...materialForm, type: e.target.value })}
                >
                  <option value="video">📹 Video</option>
                  <option value="ejercicio">📝 Ejercicio</option>
                  <option value="libro">📚 Libro</option>
                  <option value="simulador">🔬 Simulador</option>
                </select>
                <input
                  type="text"
                  placeholder="Título *"
                  value={materialForm.title}
                  onChange={(e) => setMaterialForm({ ...materialForm, title: e.target.value })}
                  required
                />
                <input
                  type="url"
                  placeholder="URL *"
                  value={materialForm.url}
                  onChange={(e) => setMaterialForm({ ...materialForm, url: e.target.value })}
                  required
                />
                <textarea
                  placeholder="Descripción (opcional)"
                  value={materialForm.description}
                  onChange={(e) => setMaterialForm({ ...materialForm, description: e.target.value })}
                  rows={3}
                />
                <div className="form-buttons">
                  <button type="submit" className="save-btn">Guardar</button>
                  <button type="button" onClick={() => setShowMaterialForm(false)} className="cancel-btn">Cancelar</button>
                </div>
              </form>
            )}

            {/* Note Form */}
            {showNoteForm && (
              <form className="note-form" onSubmit={handleAddNote}>
                <h3>Nueva Nota</h3>
                <textarea
                  placeholder="Contenido de la nota *"
                  value={noteForm.content}
                  onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                  rows={4}
                  required
                />
                <div className="note-options">
                  <label>
                    Color:
                    <input
                      type="color"
                      value={noteForm.color}
                      onChange={(e) => setNoteForm({ ...noteForm, color: e.target.value })}
                    />
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={noteForm.pinned}
                      onChange={(e) => setNoteForm({ ...noteForm, pinned: e.target.checked })}
                    />
                    Fijar arriba
                  </label>
                </div>
                <div className="form-buttons">
                  <button type="submit" className="save-btn">Guardar</button>
                  <button type="button" onClick={() => setShowNoteForm(false)} className="cancel-btn">Cancelar</button>
                </div>
              </form>
            )}

            {/* Materials by Category */}
            {['video', 'ejercicio', 'libro', 'simulador'].map(type => {
              const typeMaterials = materialsByType[type]
              if (typeMaterials.length === 0) return null
              const labels = {
                video: '📹 Videos',
                ejercicio: '📝 Ejercicios',
                libro: '📚 Libros',
                simulador: '🔬 Simuladores'
              }

              return (
                <div key={type} className="materials-category">
                  <h3>{labels[type]}</h3>
                  <div className="materials-grid">
                    {typeMaterials.map(material => (
                      <MaterialCard
                        key={material.id}
                        material={material}
                        onEdit={() => { }}
                        onDelete={handleDeleteMaterial}
                        isOwner={user?.uid === material.createdBy}
                      />
                    ))}
                  </div>
                </div>
              )
            })}

            {materials.length === 0 && !showMaterialForm && (
              <div className="empty-materials">
                <Book size={48} />
                <p>No hay materiales agregados aún</p>
                {isTutorOfCourse && <p>Usa el botón "Agregar Material" para comenzar</p>}
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {showSectionForm && (
          <div className="section-form-card">
            <h3>{editingSection ? 'Editar Sección' : 'Nueva Sección'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="day">Día de la Semana *</label>
                <select
                  id="day"
                  name="day"
                  value={formData.day}
                  onChange={handleChange}
                  required
                >
                  <option value="">Selecciona un día</option>
                  <option value="Lunes">Lunes</option>
                  <option value="Martes">Martes</option>
                  <option value="Miércoles">Miércoles</option>
                  <option value="Jueves">Jueves</option>
                  <option value="Viernes">Viernes</option>
                  <option value="Sábado">Sábado</option>
                  <option value="Domingo">Domingo</option>
                  <option value="Varios/Especial">Varios / Especial</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="date">{isEUCS ? 'Fecha (Obligatorio para EUCS) *' : 'Fecha Específica (Opcional)'}</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  disabled={loading}
                  required={isEUCS} // Requerido para EUCS
                />
              </div>

              <div className="form-group">
                <label htmlFor="startTime">Hora de Inicio *</label>
                <select
                  id="startTime"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  required
                >
                  <option value="">Selecciona hora de inicio</option>
                  <option value="7:00 AM">7:00 AM</option>
                  <option value="7:30 AM">7:30 AM</option>
                  <option value="8:00 AM">8:00 AM</option>
                  <option value="8:30 AM">8:30 AM</option>
                  <option value="9:00 AM">9:00 AM</option>
                  <option value="9:30 AM">9:30 AM</option>
                  <option value="10:00 AM">10:00 AM</option>
                  <option value="10:30 AM">10:30 AM</option>
                  <option value="11:00 AM">11:00 AM</option>
                  <option value="11:30 AM">11:30 AM</option>
                  <option value="12:00 PM">12:00 PM</option>
                  <option value="12:30 PM">12:30 PM</option>
                  <option value="1:00 PM">1:00 PM</option>
                  <option value="1:30 PM">1:30 PM</option>
                  <option value="2:00 PM">2:00 PM</option>
                  <option value="2:30 PM">2:30 PM</option>
                  <option value="3:00 PM">3:00 PM</option>
                  <option value="3:30 PM">3:30 PM</option>
                  <option value="4:00 PM">4:00 PM</option>
                  <option value="4:30 PM">4:30 PM</option>
                  <option value="5:00 PM">5:00 PM</option>
                  <option value="5:30 PM">5:30 PM</option>
                  <option value="6:00 PM">6:00 PM</option>
                  <option value="6:30 PM">6:30 PM</option>
                  <option value="7:00 PM">7:00 PM</option>
                  <option value="7:30 PM">7:30 PM</option>
                  <option value="8:00 PM">8:00 PM</option>
                  <option value="8:30 PM">8:30 PM</option>
                  <option value="9:00 PM">9:00 PM</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="endTime">Hora de Fin *</label>
                <select
                  id="endTime"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                  required
                >
                  <option value="">Selecciona hora de fin</option>
                  <option value="7:00 AM">7:00 AM</option>
                  <option value="7:30 AM">7:30 AM</option>
                  <option value="8:00 AM">8:00 AM</option>
                  <option value="8:30 AM">8:30 AM</option>
                  <option value="9:00 AM">9:00 AM</option>
                  <option value="9:30 AM">9:30 AM</option>
                  <option value="10:00 AM">10:00 AM</option>
                  <option value="10:30 AM">10:30 AM</option>
                  <option value="11:00 AM">11:00 AM</option>
                  <option value="11:30 AM">11:30 AM</option>
                  <option value="12:00 PM">12:00 PM</option>
                  <option value="12:30 PM">12:30 PM</option>
                  <option value="1:00 PM">1:00 PM</option>
                  <option value="1:30 PM">1:30 PM</option>
                  <option value="2:00 PM">2:00 PM</option>
                  <option value="2:30 PM">2:30 PM</option>
                  <option value="3:00 PM">3:00 PM</option>
                  <option value="3:30 PM">3:30 PM</option>
                  <option value="4:00 PM">4:00 PM</option>
                  <option value="4:30 PM">4:30 PM</option>
                  <option value="5:00 PM">5:00 PM</option>
                  <option value="5:30 PM">5:30 PM</option>
                  <option value="6:00 PM">6:00 PM</option>
                  <option value="6:30 PM">6:30 PM</option>
                  <option value="7:00 PM">7:00 PM</option>
                  <option value="7:30 PM">7:30 PM</option>
                  <option value="8:00 PM">8:00 PM</option>
                  <option value="8:30 PM">8:30 PM</option>
                  <option value="9:00 PM">9:00 PM</option>
                  <option value="9:30 PM">9:30 PM</option>
                </select>
              </div>

              {!isEUCS ? (
                <>
                  <div className="form-group">
                    <label htmlFor="building">Edificio *</label>
                    <select
                      id="building"
                      name="building"
                      value={formData.building}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Selecciona edificio</option>
                      <option value="1">Edificio 1</option>
                      <option value="2">Edificio 2</option>
                      <option value="3">Edificio 3</option>
                      <option value="4">Edificio 4</option>
                      <option value="5">Edificio 5</option>
                      <option value="6">Edificio 6</option>
                      <option value="Bungalow">Bungalow</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="classroom">Aula *</label>
                    <input
                      type="text"
                      id="classroom"
                      name="classroom"
                      value={formData.classroom}
                      onChange={handleChange}
                      placeholder="Ej: 307, A1, Lab 2"
                      required
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="meetingLink">Enlace de Reunión *</label>
                    <input
                      type="url"
                      id="meetingLink"
                      name="meetingLink"
                      value={formData.meetingLink}
                      onChange={handleChange}
                      placeholder="https://meet.google.com/... o Zoom"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="topic">Sílabo (Tema o Contenido) *</label>
                    <input
                      type="text"
                      id="topic"
                      name="topic"
                      value={formData.topic}
                      onChange={handleChange}
                      placeholder="Ej: Repaso límite funciones, Capítulo 3"
                      required
                    />
                  </div>
                </>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={handleCancel}
                >
                  <X size={18} />
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="save-button"
                >
                  <Save size={18} />
                  {editingSection ? 'Guardar Cambios' : 'Crear Sección'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="sections-section">
          <h2>
            <Calendar size={24} />
            Horarios de Clase
          </h2>
          {sections.length === 0 ? (
            <div className="empty-state">
              <Clock size={48} />
              <p>No hay secciones registradas</p>
              {isTutorOfCourse && (
                <button
                  className="empty-action"
                  onClick={() => {
                    setShowSectionForm(true)
                    setEditingSection(null)
                    setFormData(initialFormState)
                  }}
                >
                  Crear primera sección
                </button>
              )}
            </div>
          ) : (
            <div className="sections-grid">
              {sections.map((section) => (
                <div key={section.id} className="section-card">
                  <div className="section-header">
                    <div className="section-day">
                      <Calendar size={20} />
                      {section.day}
                    </div>
                    {isTutorOfCourse && (
                      <div className="section-actions">
                        <button
                          className="icon-button edit"
                          onClick={() => handleEdit(section)}
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="icon-button delete"
                          onClick={() => handleDeleteSection(section.id)}
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="section-time">
                    <Clock size={16} />
                    {section.date && <span className="section-date-label">{formatDate(section.date)}</span>}
                    <span className="time-range">{section.time}</span>
                  </div>
                  <div className="section-location">
                    <MapPin size={16} />
                    {section.location}
                    {section.meetingLink && (
                      <div className="eucs-section-details" style={{ marginTop: '8px', fontSize: '0.9rem', color: '#555' }}>
                        <div><strong>Enlace:</strong> <a href={section.meetingLink} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{section.meetingLink}</a></div>
                        {section.topic && <div><strong>Tema:</strong> {section.topic}</div>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CourseDetail



